import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const NumberLineJumpGame = ({ gameData, onComplete }) => {
  const [currentJumpIndex, setCurrentJumpIndex] = useState(0);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [frogPosition, setFrogPosition] = useState(null);

  const currentJump = gameData?.jumps?.[currentJumpIndex];
  const numberRange = gameData?.number_range || [0, 20];

  const generateNumberLine = () => {
    if (!currentJump) return [];
    const minVal = Math.min(currentJump.start, currentJump.target);
    const maxVal = Math.max(currentJump.start, currentJump.target);
    const start = Math.max(0, minVal - 3);
    const end = maxVal + 4;

    const numbers = [];
    for (let i = start; i <= end; i++) {
      numbers.push(i);
    }
    return numbers;
  };
  
  const numberLine = generateNumberLine();

  useEffect(() => {
    if (currentJump) {
      setFrogPosition(currentJump.start);
      setSelectedNumber(null);
      setFeedback(null);
    }
  }, [currentJumpIndex, currentJump]);

  const handleNumberClick = (number) => {
    if (feedback) return;
    
    setSelectedNumber(number);
    setFrogPosition(number);
    triggerHaptic(30);
    
    const isCorrect = number === currentJump.target;
    
    if (isCorrect) {
      setFeedback('correct');
      const pointsToAdd = gameData.points_per_jump || 20;
      const newScore = score + pointsToAdd;
      setScore(newScore);
      triggerHaptic([100, 50, 100]);
      
      setTimeout(() => {
        if (currentJumpIndex + 1 >= gameData.jumps.length) {
          triggerHaptic([200, 100, 200, 100, 300]);
          onComplete?.(newScore);
        } else {
          setCurrentJumpIndex(prev => prev + 1);
        }
      }, 3000);
    } else {
      setFeedback('wrong');
      triggerHaptic([50, 50, 50]);
      setTimeout(() => {
        setFeedback(null);
        setFrogPosition(currentJump.start);
        setSelectedNumber(null);
      }, 2000);
    }
  };

  if (!currentJump) {
    return (
      <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-emerald-100 animate-pulse bg-white">
        <div className="text-6xl md:text-[100px] mb-8">🐸</div>
        <h3 className="text-base md:text-3xl font-black text-emerald-900 tracking-tighter uppercase">Ruošiamas šuolių takas...</h3>
      </div>
    );
  }

  const progress = (currentJumpIndex / gameData.jumps.length) * 100;

  const hud = (
    <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-emerald-50">
      <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
        <div className="flex items-center gap-2 md:gap-4">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-emerald-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
            🐸
          </div>
          <div>
            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
              Šuolio <span className="text-emerald-600">Meistras</span>
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
            <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Energija</p>
            <p className="text-sm md:text-2xl font-black text-emerald-600 leading-none">⭐ {score}</p>
          </div>
          <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
            <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Etapas</p>
            <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentJumpIndex + 1}/{gameData.jumps.length}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
        <div 
          className="h-full bg-gradient-to-r from-emerald-400 via-green-500 to-teal-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(16,185,129,0.4)]"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );

  const choices = (
    <div className="space-y-4 md:space-y-12 py-4 md:py-12 flex flex-col items-center">
        <div className="bg-white rounded-[2rem] md:rounded-[3.5rem] p-6 md:p-12 shadow-2xl border-4 border-emerald-50 relative group inline-block px-8 md:px-20 text-center">
            <div className="absolute top-0 right-0 p-4 text-4xl md:text-7xl opacity-[0.05] select-none pointer-events-none group-hover:scale-125 transition-transform">📋</div>
            <h4 className="text-[8px] md:text-[10px] font-black text-emerald-400 uppercase tracking-[0.6em] mb-4 md:mb-6">Instrukcija</h4>
            <h3 className="text-lg md:text-5xl font-black text-slate-900 tracking-tighter uppercase leading-tight">
                {currentJump.instruction}
            </h3>
        </div>
        <div className="flex items-center justify-center gap-3 animate-pulse">
            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
            <p className="text-xs md:text-xl font-bold text-slate-500 italic font-serif">"Bakstelėk skaičių tiesėje!"</p>
        </div>
    </div>
  );

  const targets = (
    <div className="bg-slate-800 rounded-2xl md:rounded-[5rem] p-4 md:p-10 shadow-2xl border-4 md:border-8 border-slate-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.1)_0%,transparent_100%)] pointer-events-none" />
        
        <div className="flex justify-start items-end gap-2 md:gap-4 min-w-max pb-12 md:pb-20 px-8 md:px-16 relative z-10 overflow-x-auto no-scrollbar">
            {numberLine.map((number) => {
                const isStart = number === currentJump.start;
                const isTarget = number === currentJump.target;
                const isFrogHere = number === frogPosition;
                const isSelected = number === selectedNumber;

                return (
                    <div key={number} className="flex flex-col items-center gap-2 md:gap-4 transition-all duration-700 ease-out">
                        <div className={`h-16 w-16 md:h-32 md:w-32 flex items-center justify-center transition-all duration-700 ${isFrogHere ? 'scale-110 md:scale-125 opacity-100' : 'scale-50 translate-y-10 opacity-0'}`}>
                            <span className="text-2xl md:text-7xl animate-bounce drop-shadow-2xl">🐸</span>
                        </div>

                        <button
                            onClick={() => handleNumberClick(number)}
                            disabled={!!feedback}
                            className={`
                                w-14 h-14 md:w-32 md:h-32 rounded-xl md:rounded-[3rem] text-lg md:text-5xl font-black transition-all duration-300 border-b-4 md:border-b-[12px] relative group
                                ${isStart && !isSelected ? 'bg-indigo-600 text-white border-indigo-800' : 
                                  isSelected && feedback === 'correct' ? 'bg-emerald-500 text-white border-emerald-700 scale-110' :
                                  isSelected && feedback === 'wrong' ? 'bg-rose-500 text-white border-rose-700 animate-shake' :
                                  'bg-slate-600 text-slate-100 border-slate-500 hover:bg-slate-500 hover:text-white'}
                                active:translate-y-1 md:active:translate-y-2 active:border-b-0
                            `}
                        >
                            <span className="relative z-10">{number}</span>
                        </button>
                        
                        <div className="h-10 flex flex-col items-center">
                            {isStart && (
                                <span className="text-[6px] md:text-[10px] font-black text-indigo-400 uppercase tracking-widest bg-indigo-900/50 px-2 py-1 md:px-5 md:py-2 rounded-full border border-indigo-500/30">Startas</span>
                            )}
                            {isTarget && !feedback && (
                                <div className="w-2 h-2 md:w-4 md:h-4 bg-emerald-500 rounded-full animate-ping mt-2" />
                            )}
                        </div>
                    </div>
                );
            })}
        </div>

        <div className="absolute bottom-[68px] md:bottom-[150px] left-0 right-0 h-2 md:h-4 bg-gradient-to-r from-transparent via-slate-800 to-transparent rounded-full mx-10 md:mx-24 z-0 border-y md:border-y-2 border-white/5 shadow-2xl"></div>
    </div>
  );

  return (
    <>
      <DragDropLayout 
        hud={hud}
        choices={choices}
        targets={targets}
        variant="bottom"
      />

      {feedback && (
        <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
          <div className={`
            max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
            ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
          `}>
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
              {feedback === 'correct' ? '🏆' : '🕵️‍♂️'}
            </div>
            
            <div className="text-center space-y-4 pt-4">
              <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                {feedback === 'correct' ? 'TIKSLUS ŠUOLIS!' : 'KLAIDA!'}
              </h2>

              <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-center italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                <p className="text-sm md:text-2xl font-bold text-slate-700 leading-relaxed">
                  {feedback === 'correct' ? 'Varlytė sėkmingai pasiekė savo tikslą! Puikus matematinis skaičiavimas.' : 'Oi! Panašu, kad šuolio trajektorija buvo netiksli. Pabandykime dar kartą!'}
                </p>
              </div>

              <div className="flex items-center justify-center gap-4">
                <div className="flex gap-2">
                  <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce" />
                  <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                  <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                </div>
                <span className="text-[10px] md:text-sm font-black text-slate-300 uppercase tracking-[0.3em]">
                  {feedback === 'correct' ? 'Kitas šuolis...' : 'Grįžtame...'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NumberLineJumpGame;
